<script type="text/ecmascript-6">
import {
  isEmpty as _isEmpty,
  debounce as _debounce,
  find as _find,
  has as _has,
  map as _map
} from "lodash";
import { mapGetters, mapState } from "vuex";
import GeneralMixin from "JS/mixins/campaign/general";
import LegislatorsMixin from "JS/mixins/campaign/legislators";
import Pusher from "pusher-js";
import Pluralize from "pluralize";

import * as ACTIONS from "Constants/campaign/actionTypes";
import * as SUBJECT_DISPLAY_TYPES from "Constants/campaign/emailSubjectDisplayTypes";

import SubjectLine from "./SubjectLine.vue";

// conversational
import CardPrimary from "../conversational/card/CardPrimary.vue";
import FieldText from "../conversational/field/FieldText.vue";
import FieldSelect from "../conversational/field/FieldSelect.vue";
import LegislatorsList from "../conversational/action/LegislatorsList.vue";
import LegislatorImage from "../conversational/action/LegislatorImage.vue";
import LegislatorName from "../conversational/action/LegislatorName.vue";

export default {
  name: "LegislatorResults",
  components: {
    CardPrimary,
    FieldText,
    FieldSelect,
    LegislatorsList,
    LegislatorImage,
    LegislatorName,
    SubjectLine
  },
  mixins: [GeneralMixin, LegislatorsMixin],
  props: {
    advancedAccessibility: {
      type: Boolean,
      default: false
    },
    advocateInfo: {
      required: true,
      type: Object
    },
    andLabel: {
      type: String,
      required: true
    },
    callInstructions: {
      type: Object,
      required: true
    },
    callToActionHeading: {
      type: Object,
      required: true
    },
    campaignDesignColors: {
      type: Object,
      default: () => {}
    },
    charactersRemainingLabel: {
      type: String,
      required: true
    },
    contactLegislatorsPrompt: {
      type: Object,
      required: true
    },
    contactPhoneScript: {
      type: String,
      required: true
    },
    contentLabelSalutation: {
      type: String,
      required: true
    },
    defaultPhotoUrl: {
      type: String,
      required: true
    },
    emailLocked: {
      type: Boolean,
      required: true
    },
    emailEditRequired: {
      type: Boolean,
      required: true
    },
    greetingPrefix: {
      type: String,
      required: true
    },
    groupedCallInstructions: {
      type: Array,
      required: true
    },
    hasOneStep: {
      type: Boolean,
      required: true
    },
    headlineColor: {
      type: Object,
      default: () => {}
    },
    isMultistep: {
      type: Boolean,
      required: true
    },
    includeWebIntentMedia: {
      type: Boolean,
      required: true
    },
    isMobile: {
      type: Boolean,
      required: true
    },
    legislatorHeadings: {
      type: Object,
      required: true
    },
    legislatorHeadingsCollapseLabel: {
      type: String,
      required: true
    },
    loadStart: {
      type: String,
      default: ""
    },
    messagesPlaceHolder: {
      type: Object,
      required: true
    },
    messagesSuggestionPrompt: {
      type: Object,
      required: true
    },
    multiMessageLabels: {
      type: Object,
      required: true
    },
    noLegislatorsFoundErrorMessage: {
      type: String,
      required: true
    },
    ofLabel: {
      type: String,
      required: true
    },
    scriptHeadline: {
      type: String,
      required: true
    },
    seeDetailedResultsPrompt: {
      type: String,
      required: true
    },
    shortLegislatorListLength: {
      type: Number,
      required: true
    },
    showLegislatorDetailsLabel: {
      type: String,
      required: true
    },
    showRemainingLegislatorsLabel: {
      type: String,
      required: true
    },
    submitButtonLabels: {
      type: Object,
      required: true
    },
    tweetPostedLabel: {
      type: String,
      required: true
    },
    twitterActionFooterLabel: {
      type: String,
      required: true
    },
    useTwitterWebIntents: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      context: "legislators",
      scriptOpen: false,
      showButtonHover: false
    };
  },
  computed: {
    ...mapGetters({
      currentActionStep: "navigation/currentActionStep",
      currentMessage: "legislators/currentMessage",
      activeCallLegislatorId: "legislators/activeCallLegislatorId",
      legislatorsCalled: "legislators/legislatorsCalled",
      nextCallLegislatorId: "legislators/nextCallLegislatorId",
      showOneStepClosing: "legislators/showOneStepClosing",
      emailMessageModified: "legislators/emailMessageModified"
    }),
    ...mapState("campaign", {
      emailSubjectLineDisplayType: "emailSubjectLineDisplayType"
    }),
    shouldInstantiatePusher() {
      const isCallAction = this.currentAction === ACTIONS.CALL;
      return isCallAction && this.legislatorsPulled && !this.pusherInstantiated;
    },
    activeCallLegislators() {
      return this.currentLegislatorMessage.legislators.slice().reverse();
    },
    activeCallLegislator() {
      const callLegislator = _find(
        this.activeCallLegislators,
        legislator => legislator.id.p2a === this.activeCallLegislatorId
      );

      return this.getLegislatorDetails(callLegislator);
    },
    advocateId() {
      return this.$store.getters["advocate/id"];
    },
    apiLegislatorResults() {
      return this.$store.getters[`${this.context}/apiLegislatorResults`];
    },
    buttonColors() {
      const buttonColors = {
        background: this.campaignDesignColors.submitButtonBackground,
        color: this.campaignDesignColors.submitButtonText
      };
      if (this.showButtonHover && !this.isMobile) {
        buttonColors.background = this.campaignDesignColors.submitButtonHover;
      }
      return buttonColors;
    },
    callInProgress() {
      return this.$store.getters["legislators/callInProgress"];
    },
    editMessageDisabled() {
      // If this should be disabled *and* we have advancedAccessibility enabled, make this readonly, not disabled
      return (
        this.currentAction === ACTIONS.EMAIL &&
        this.emailLocked &&
        !this.advancedAccessibility
      );
    },
    editMessageReadonly() {
      return (
        this.currentAction === ACTIONS.EMAIL &&
        this.emailLocked &&
        this.advancedAccessibility
      );
    },
    legislatorsFound() {
      return this.legislatorMessages.length !== 0;
    },
    legislatorsListExpanded() {
      const legislatorLists = this.$store.getters[
        `${this.context}/expandedLegislatorLists`
      ].length;
      return legislatorLists > 0;
    },
    legislatorListHeading() {
      if (this.currentAction === ACTIONS.CALL) {
        const state = this.callInProgress ? "active" : "inactive";
        return this.legislatorHeadings[this.currentAction][state];
      }

      return this.legislatorHeadings[this.currentAction];
    },
    loading() {
      return this.$store.getters[`${this.context}/loading`];
    },
    hasAdvocateBadge() {
      return this.advocateInfo.id !== undefined;
    },
    getMessagesPlaceHolder() {
      return (
        this.messagesPlaceHolder[this.currentAction] ||
        "Let your officials know why this matters to you"
      );
    },
    pusherInstantiated() {
      return this.$store.getters["legislators/pusherInstantiated"];
    },
    showOneStep() {
      return this.hasOneStep && this.currentAction === ACTIONS.EMAIL;
    },
    showClosing() {
      return !this.hasOneStep || this.showOneStepClosing;
    },
    showWebIntentButtons() {
      return (
        this.useTwitterWebIntents && this.currentAction === ACTIONS.TWITTER
      );
    },
    messageCountLabel() {
      const current = this.currentMessage + 1;
      const total = Object.keys(this.legislatorMessages).length;
      let label = `${current} ${this.ofLabel} ${total}`;

      if (!this.isMobile) {
        label += ` ${this.multiMessageLabels[this.currentAction]}`;
      }

      return label;
    },
    nextCallLegislator() {
      const callLegislator = _find(
        this.activeCallLegislators,
        legislator => legislator.id.p2a === this.nextCallLegislatorId
      );

      return this.getLegislatorDetails(callLegislator);
    },
    twitterCharactersRemaining() {
      return this.$store.getters[`${this.context}/twitterCharactersRemaining`];
    },
    showCookieBadge() {
      return (
        !this.legislatorsPulled &&
        this.hasAdvocateBadge &&
        !this.completedAllActions
      );
    },
    showSubjectLine() {
      return (
        this.emailSubjectLineDisplayType !==
          SUBJECT_DISPLAY_TYPES.HIDDEN_SUBJECT &&
        this.currentAction === ACTIONS.EMAIL
      );
    },
    showMessageModificationRequirement() {
      return this.emailEditRequired && this.currentAction === ACTIONS.EMAIL;
    }
  },
  inject: {
    eventsBus: { from: "eventsBus" },
    twitterHelper: { from: "twitterHelper" },
    axiosHelper: { from: "axiosHelper" }
  },
  watch: {
    // Populate merge variables in the advocate message
    currentLegislatorMessage(value) {
      if (
        this.isMultistep &&
        value.message.body &&
        value.message.body.length &&
        this.checkObjectHasNonEmptyValues(this.mergeVariables)
      ) {
        value.message.body = this.populateMergeVariables(
          value.message.body,
          this.mergeVariables
        );
      }
    },
    shouldInstantiatePusher() {
      this.instantiatePusher();
    }
  },
  mounted() {
    this.onMounted();
  },
  methods: {
    activeCallBackground(legId) {
      const status = this.getLegStatus(legId);
      return status === "active"
        ? this.campaignDesignColors.activeCallBackground
        : "";
    },
    activeCallBackgroundStyle(legId) {
      const background = this.activeCallBackground(legId);
      return _isEmpty(background) ? "" : `background: ${background};`;
    },
    assignHoverColor(showHover) {
      this.showButtonHover = showHover;
    },
    onMounted() {
      this.instantiatePusher();
      // Check if All calls to legislators were completed
      // TODO:: Mark the call as completed as soon as all the
      // calls to the legislators have finished
      this.checkIfAllCallsCompleted();
      // Listen to the event emitted when advocates click on find my legislators button
      this.eventsBus.$on("findLegislators", payload => {
        this.debounceFetchLegislators(payload);
      });

      this.setLegislatorsErrorMessage("");
    },
    // Return true if object contains non-falsy values
    checkObjectHasNonEmptyValues(object) {
      const objectLength = Object.values(object).filter(Boolean).length;
      return objectLength > 0;
    },
    checkIfAllCallsCompleted() {
      const legCallStatuses = this.legislatorsCalled;
      if (_isEmpty(legCallStatuses)) {
        return this.$store.dispatch("legislators/callsCompleted", false);
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const prop in legCallStatuses) {
        if (legCallStatuses[prop] !== "COMPLETED") {
          return this.$store.dispatch("legislators/callsCompleted", false);
        }
      }
      return this.$store.dispatch("legislators/callsCompleted", true);
    },
    fetchLegislators(background = false, address = {}) {
      // Make sure we reset the legislatorsPulled state since we are
      // about to fetch legislators for current action
      this.$store.dispatch("legislators/legislatorsPulled", false);

      if (!this.legislatorsFound || background || this.isMultistep) {
        this.geosearchLegislators(address);
      }

      if (!background) {
        this.$store.dispatch("legislators/legislatorsPulled", true);
      }
    },
    generateAbbreviatedName(legislator) {
      let name = legislator.name_full;

      if (legislator.name_last) {
        name = legislator.name_last;
      }

      if (legislator.title) {
        name = `${this.generateAbbreviatedTitle(legislator.title)} ${name}`;
      }

      return name;
    },
    generateAbbreviatedTitle(title) {
      return ["Representative", "Senator"].includes(title)
        ? `${title.substring(0, 3)}.`
        : title;
    },
    generateAffiliation(party, state) {
      let affiliation = "";

      if (party) {
        affiliation = state ? `${party}-${state}` : `${party}`;
      } else if (state) {
        affiliation = `${state}`;
      }

      return affiliation;
    },
    generateLegislatorNameList(legislators = []) {
      const legislatorNames = legislators.map(legislator => {
        const abbreviatedTitle = this.generateAbbreviatedTitle(
          legislator.title
        );
        return `${abbreviatedTitle} ${legislator.name_full}`;
      });
      const legislatorNameString = legislatorNames.join(", ");
      return `${legislatorNameString.slice(
        0,
        this.shortLegislatorListLength
      )}...`;
    },
    getLegislatorListExpandLabel(legislators) {
      const remainingLegislatorsCount = legislators.length - 1;
      const andLabel = this.andLabel.toLowerCase();
      const showLegislatorDetailsLabel = this.showLegislatorDetailsLabel.toLowerCase();
      let remainingLegislatorsLabel = this.showRemainingLegislatorsLabel.toLowerCase();

      if (remainingLegislatorsCount === 0) {
        return showLegislatorDetailsLabel;
      }

      if (remainingLegislatorsCount > 1) {
        remainingLegislatorsLabel = Pluralize.plural(remainingLegislatorsLabel);
      }

      return `${andLabel} ${remainingLegislatorsCount} ${remainingLegislatorsLabel}`;
    },
    getLegislatorSalutation(message) {
      const firstLegislatorGreeting = this.getLegislatorGreetingName(message);
      const remainingLegislatorsCount = message.legislators.length - 1;
      const andLabel = this.andLabel.toLowerCase();
      let remainingLegislatorsLabel = this.showRemainingLegislatorsLabel.toLowerCase();

      if (remainingLegislatorsCount === 0) {
        return firstLegislatorGreeting;
      }

      if (remainingLegislatorsCount > 1) {
        remainingLegislatorsLabel = Pluralize.plural(remainingLegislatorsLabel);
      }

      return `${firstLegislatorGreeting} ${andLabel} ${remainingLegislatorsCount} ${remainingLegislatorsLabel}`;
    },
    getLegStatus(legId) {
      if (this.currentAction !== ACTIONS.CALL) {
        return "";
      }
      const legStatus = this.legislatorsCalled[legId] || "";
      const legislatorStatuses = {
        "IN-PROGRESS": "active",
        COMPLETED: "completed"
      };
      return legislatorStatuses[legStatus.toUpperCase()] || "";
    },
    handlePusherData({ campaignid, advocateid, status, legid, nextlegid }) {
      // TODO:: Pass in campaignId as a prop from Sombra and reference it here instead of the window object
      if (
        campaignid === window.P2A.campaign.id &&
        this.advocateId === advocateid
      ) {
        if (status === "PATCH-THROUGH-CALL-STARTED") {
          // account for delay in receiving call once the request is sent
          setTimeout(() => {
            this.$store.dispatch("legislators/callInProgress", true);
          }, 4500);
          return;
        }

        if (
          status === "PATCH-THROUGH-CALL-COMPLETED" ||
          status === "PATCH-THROUGH-CALL-VOICEMAIL"
        ) {
          this.$store.dispatch("legislators/callsCompleted", true);
          return;
        }

        this.$store.dispatch("legislators/legislatorsCalled", {
          [legid]: status
        });

        this.$store.dispatch("legislators/activeCallLegislatorId", legid);
        this.$store.dispatch("legislators/nextCallLegislatorId", nextlegid);

        this.checkIfAllCallsCompleted();
      }
    },
    getApiLegislatorMessageBody(messageId) {
      const currentApiLegislatorResults =
        this.apiLegislatorResults[this.currentAction] || [];
      const message =
        _find(
          currentApiLegislatorResults,
          legislatorMessage => legislatorMessage.message.id === messageId
        ) || {};
      const { message: messageRecord = {} } = message;
      return (messageRecord.body || "").trim();
    },
    getLegislatorGreetingName(message) {
      const name = this.generateAbbreviatedName(message.legislators[0]);
      return this.currentAction === ACTIONS.EMAIL
        ? `${this.greetingPrefix} ${name}`
        : name;
    },
    isLegislatorListExpanded(messageId) {
      return this.expandedLegislatorLists.includes(messageId);
    },
    handleSaveMessage(messageId, value) {
      if (this.currentAction === ACTIONS.TWITTER) {
        // eslint-disable-next-line no-multi-assign
        const twitterCharObject = (this.twitterCharactersRemaining[
          messageId
        ] = this.twitterHelper.countRemainingCharacters(value));
        this.$store.dispatch(
          `${this.context}/twitterCharactersRemaining`,
          twitterCharObject
        );
      } else if (
        this.currentAction === ACTIONS.EMAIL &&
        this.emailEditRequired
      ) {
        if (this.emailMessageModified) {
          // If the message has already been modified, check if the advocate reverted it to the default
          if (!this.checkModifiedMessages()) {
            this.$store.dispatch(`${this.context}/emailMessageModified`, false);
          }
        } else if (this.checkModifiedMessages()) {
          this.$store.dispatch(`${this.context}/emailMessageModified`, true);
        }
      }
    },
    checkModifiedMessages() {
      const messagesNotModified =
        _find(this.legislatorMessages, legislatorMessage => {
          const campaignMessage = this.getApiLegislatorMessageBody(
            legislatorMessage.message.id
          );
          const advocateMessage = legislatorMessage.message.body || "";
          return campaignMessage.trim() === advocateMessage.trim();
        }) || {};
      return _isEmpty(messagesNotModified);
    },
    showLegislators(legislatorMessage) {
      return (
        this.currentAction === ACTIONS.EMAIL &&
        _has(legislatorMessage, "legislators") &&
        legislatorMessage.legislators.length > 0
      );
    },
    instantiatePusher() {
      if (!this.shouldInstantiatePusher) {
        return false;
      }
      // eslint-disable-next-line no-undef
      const pusher = new Pusher(window.P2A.Pusher.appKey, {
        cluster: window.P2A.Pusher.clusterId
      });
      this.setInitialCallStatuses();

      const pusherChannel = pusher.subscribe("ac-calls");
      pusherChannel.bind("call-updated", data => {
        this.handlePusherData(data);
      });
      this.$store.dispatch("legislators/pusherInstantiated", true);
      return true;
    },
    isInvalidTweet(messageId) {
      return this.twitterCharactersRemaining[messageId] < 0;
    },
    setInitialCallStatuses() {
      const currentLegislatorResults =
        this.legislatorResults[this.currentAction] || [];
      const legislators = !_isEmpty(currentLegislatorResults)
        ? currentLegislatorResults[0].legislators
        : [];

      legislators.forEach(legislator => {
        const legid = legislator.id.p2a;
        this.$store.dispatch("legislators/legislatorsCalled", { [legid]: "" });
      });
    },
    toggleDetailView(messageId) {
      const legIndex = this.expandedLegislatorLists.indexOf(messageId);
      const expandedLegislatorListsCopy = this.expandedLegislatorLists;

      if (legIndex > -1) {
        expandedLegislatorListsCopy.splice(legIndex, 1);
      } else {
        expandedLegislatorListsCopy.push(messageId);
      }

      this.$store.dispatch(
        `${this.context}/expandedLegislatorLists`,
        expandedLegislatorListsCopy
      );

      this.setModalState(this.legislatorsListExpanded);
    },
    toggleScriptOpen() {
      if (this.currentAction === ACTIONS.CALL) {
        this.scriptOpen = !this.scriptOpen;
      }
    },
    cookieSignoff(signOff, variables) {
      let signOffMessage = signOff;
      if (!signOffMessage) {
        return signOffMessage;
      }
      Object.entries(variables).forEach(variable => {
        const [key, value] = variable;
        if (key === "advFirst") {
          signOffMessage = signOffMessage.replace(`[@${key}]`, value);
        }
        if (key === "advLast") {
          signOffMessage = signOffMessage.replace(
            `[@${key}]`,
            `${value.charAt(0).toUpperCase()}.`
          );
        }
        signOffMessage = signOffMessage.replace(`[@${key}]`, "");
      });
      return signOffMessage.trim();
    },
    retrieveSignoff(signOff, variables) {
      if (this.hasAdvocateBadge) {
        return this.cookieSignoff(signOff, variables);
      }
      return this.populateMergeVariables(signOff, variables);
    },
    getLegislatorDetails(legislator) {
      return legislator
        ? {
            id: legislator.id.p2a,
            photo: this.getLegislatorPhoto(legislator),
            name: this.generateAbbreviatedName(legislator),
            affiliation: this.generateAffiliation(
              legislator.party,
              legislator.state
            )
          }
        : null;
    },
    getLegislatorList(legislators) {
      return _map(legislators, this.getLegislatorDetails);
    },
    getLegislatorPhoto(legislator) {
      return legislator.photo || this.defaultPhotoUrl;
    },
    handleInvalidLegislatorPhoto(legislator) {
      legislator.photo = this.defaultPhotoUrl;
    },
    getTweetRemainingCharacters(messageId, tweet) {
      const computedRemainingCharacters = this.twitterCharactersRemaining[
        messageId
      ];
      if (!_isEmpty(computedRemainingCharacters)) {
        return computedRemainingCharacters;
      }
      return this.twitterHelper.countRemainingCharacters(tweet);
    },
    hasTweetBeenPosted(messageId) {
      return this.tweetsPosted.indexOf(messageId) > -1;
    },
    postTweet(legislatorMessage) {
      const tweetsPosted = this.tweetsPosted.slice();
      tweetsPosted.push(legislatorMessage.message.id);

      this.setTweetsPosted(tweetsPosted);
      this.openWebIntent(
        legislatorMessage.message.body,
        this.getTweetMedia(legislatorMessage)
      );
    },
    getTweetMedia(legislatorMessage) {
      let tweetMedia = null;

      if (!this.includeWebIntentMedia) {
        return null;
      }

      try {
        const media = JSON.parse(legislatorMessage.message.media);

        if (Array.isArray(media) && media.length) {
          tweetMedia = `&url=${media[0]}`;
        }
      } catch (e) {
        tweetMedia = null;
      }

      return tweetMedia;
    },
    tweetButtonLabel(messageId) {
      if (this.hasTweetBeenPosted(messageId)) {
        return this.tweetPostedLabel;
      }

      return this.submitButtonLabels[this.currentAction];
    },
    // eslint-disable-next-line func-names
    debounceFetchLegislators: _debounce(function(payload) {
      this.fetchLegislators(payload.background, payload.address);
    }, 100)
  }
};
</script>
